// "Can this unit be given this card at all?", asked before the player has picked a target.
//
// Shared by the order deck (src/ui/deck/Deck.tsx) and the radial order menu (src/ui/radial), so a card
// that is greyed in one is greyed in the other for the same stated reason. The real check runs again in
// `useStore.issue` with the target the player actually chose; this one only ever reports what the unit's
// type and state already decide.

import { isLegal } from '../../sim/deck';
import type { CardDef, Graphic, Target, Unit } from '../../sim/types';
import type { Grid } from '../../sim/grid';

/**
 * A target that satisfies the card's target stage, so `isLegal` reports only unit type and unit state
 * rather than "needs a point on the map". Probing with `{kind:'none'}` and filtering the target-kind
 * complaint out of the reason would answer the same question; handing it a satisfying target is the
 * same check without the string matching.
 */
export function probeTarget(def: CardDef, unit: Unit, graphics: Graphic[], grid: Grid): Target {
  switch (def.targetKind) {
    case 'point': return { kind: 'point', cell: grid.cellOfCm(unit.x, unit.y) };
    case 'graphic': return { kind: 'graphic', graphicId: graphics[0]?.id };
    case 'unit': return { kind: 'unit', unitId: unit.id };
    case 'text': return { kind: 'text', text: 'probe' };
    case 'contact': return { kind: 'contact' };
    case 'none': return { kind: 'none' };
  }
}

/** Why this card cannot be given to this unit right now, or undefined when it can. */
export function cardReason(
  def: CardDef, unit: Unit, grid: Grid, graphics: Graphic[], units: Unit[],
): string | undefined {
  const legal = isLegal(def.id, unit, probeTarget(def, unit, graphics, grid), grid, graphics, units);
  return legal.ok ? undefined : legal.reason;
}
